// CreateTypeSection.jsx

import React from 'react';

// CSS
import './CreateTypeSection.css';

// Components
import CreateTypeFields from 'components/addmodal/sections/createtype/CreateTypeFields';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';

/**
 * CreateTypeSection
 * 
 * This component renders the form for creating a new node type within the AddModal. It includes
 * fields for selecting an icon and entering a label and plural label.
 * It also displays helper text and validation errors as needed.
 * 
 * @param {string} iconKey - The key of the currently selected icon for the type.
 * @param {function} setIconKey - Function to call when the icon selection changes.
 * @param {boolean} isSavingType - Whether the type is currently being saved, used to disable inputs.
 * @param {boolean} canSaveType - Whether the form is valid and can be submitted.
 * @param {string} label - The current value of the label input field.
 * @param {function} setLabel - Function to call when the label input changes.
 * @param {string} labelHelperText - Helper text or error message to display below the label input.
 * @param {function} onCreateType - Function to call when the form is submitted to create the type.
 * @param {string} labelPlural - The current value of the plural label input field.
 * @param {function} onLabelPluralChange - Function to call when the plural label input changes.
 * @param {string} pluralHelperText - Helper text or error message to display below the plural label input.
 * @param {string} description - Optional description for the node type.
 * @param {function} setDescription - Function to call when the description input changes.
 * 
 * @returns {ReactNode} The rendered component.
 */
const CreateTypeSection = React.memo(({
  iconKey,
  setIconKey,
  isSavingType,
  canSaveType,
  label,
  setLabel,
  labelHelperText,
  onCreateType,
  labelPlural,
  onLabelPluralChange,
  pluralHelperText,
  onLabelBlur,
  onLabelPluralBlur,
  description,
  setDescription,
  defaultView,
  setDefaultView,
  onSubmit,
  canSubmit,
  submitButtonLabel = 'Save',
  submittingButtonLabel = 'Saving...',
  showSubmitButton = true,
  autoFocusLabel = true,
}) => {

  const resolvedOnSubmit = onSubmit || onCreateType;
  const resolvedCanSubmit = typeof canSubmit === 'boolean' ? canSubmit : canSaveType;

  // ----- RENDER ----- //

  return (
    <>
      <CreateTypeFields
        iconKey={iconKey}
        setIconKey={setIconKey}
        isSavingType={isSavingType}
        label={label}
        setLabel={setLabel}
        labelHelperText={labelHelperText}
        labelPlural={labelPlural}
        pluralHelperText={pluralHelperText}
        description={description}
        setDescription={setDescription}
        defaultView={defaultView}
        setDefaultView={setDefaultView}
        autoFocusLabel={autoFocusLabel}
        onLabelBlur={onLabelBlur}
        onLabelPluralBlur={onLabelPluralBlur}
        onLabelPluralChange={onLabelPluralChange}
        onSubmit={resolvedOnSubmit}
      />

      {showSubmitButton && (
        <ModalButtons>
          <Button
            label={isSavingType ? submittingButtonLabel : submitButtonLabel}
            enabled={resolvedCanSubmit}
            onClick={resolvedOnSubmit}
          />
        </ModalButtons>
      )}
    </>
  );

});

// Dev Tools
CreateTypeSection.displayName = 'CreateTypeSection';

export default CreateTypeSection;
